import React from 'react';
import { observer } from 'mobx-react';
import { ConnectionAudioBridgeFormVM } from './connection-audio-bridge-form.viewmodel';
import { FieldKey, FORM_CONFIG } from './connection-audio-bridge-form.config';
import { CommonPopupBodyFooterLayout } from '../../../../../../global/common-popup/common-popup-body-footer-layout';
import { DefaultCloseSubWindowButton } from '../../../../../../global/sub-window/common/common-close-sub-window-buttons';
import { cancelText, submitText } from '../../../../../../../constants/common-labels.constants';
import { FormFieldInput } from '../../../../../../global/form/form-field-input/form-field-input';
import Text from '../../../../../../global/text/text';
import { AUTO_SYNC_ENABLED_CONNECTION_LABEL_TEXT, AUTOMATICALLY_RECORD_ENABLED_CONNECTION_LABEL_TEXT } from './texts';
import { EnablementStatus } from '../../../../../../../constants/common.constants';

export type ConnectionTemplateFormPresentationProps = {
    vm: ConnectionAudioBridgeFormVM;
};

export const ConnectionAudioBridgeFormPresentation = observer(({ vm }: ConnectionTemplateFormPresentationProps): JSX.Element => {
    const {
        type,
        label,
        provider,
        conferenceMode,
        audioProviderId,
        meetingUniqueReference,
        autoSyncEnabled,
        recordCalls
    } = vm.getFormFields(FieldKey);

    return (
        <CommonPopupBodyFooterLayout
            body={
                <form className='oe-form oe-form--connection-template'>
                    <FormFieldInput.Text
                        field={type}
                        labelDescriptor={FORM_CONFIG[FieldKey.TYPE].labelDescriptor}
                        readOnly
                        disabled
                    />

                    <FormFieldInput.Text field={label} labelDescriptor={FORM_CONFIG[FieldKey.LABEL].labelDescriptor} withError />

                    <FormFieldInput.Select
                        field={meetingUniqueReference}
                        labelDescriptor={FORM_CONFIG[FieldKey.MEETING_UNIQUE_REFERENCE].labelDescriptor}
                        customOptions={
                            FORM_CONFIG[FieldKey.MEETING_UNIQUE_REFERENCE].valueOptions.map((item, i) => {
                                const isObject = typeof item === 'object' && item != null;
                                return <option value={isObject ? item.value : item as number} key={i}>{isObject ? item.label : item}</option>;
                            })
                        }
                        fieldClassName='m-bottom-20px'
                    />

                    <FormFieldInput.Select
                        field={provider}
                        labelDescriptor={FORM_CONFIG[FieldKey.PROVIDER].labelDescriptor}
                        options={vm.providers}
                        disabled
                        withError
                    />

                    <FormFieldInput.Select
                        field={audioProviderId}
                        labelDescriptor={FORM_CONFIG[FieldKey.AUDIO_PROVIDER_ID].labelDescriptor}
                        options={vm.numberSets}
                        withError
                        placeholderOption
                    />

                    <FormFieldInput.Select
                        field={conferenceMode}
                        labelDescriptor={FORM_CONFIG[FieldKey.CONFERENCE_MODE].labelDescriptor}
                        options={vm.conferenceModeOptions}
                        withError
                    />

                    <FormFieldInput.Toggle
                        isSelected={recordCalls.value as boolean}
                        toggle={() => recordCalls.setValue(!recordCalls.value)}
                        text={<Text
                            {...AUTOMATICALLY_RECORD_ENABLED_CONNECTION_LABEL_TEXT}
                            values={{
                                prefix: recordCalls.value ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                            }}
                        />}
                    />

                    {vm.isV2ConnectionVersion() && !vm.isLimitReached && <FormFieldInput.Toggle
                        isSelected={autoSyncEnabled.value as boolean}
                        toggle={() => autoSyncEnabled.setValue(!autoSyncEnabled.value )}
                        text={<Text
                            {...AUTO_SYNC_ENABLED_CONNECTION_LABEL_TEXT}
                            values={{
                                prefix: autoSyncEnabled.value ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                            }}
                        />}
                    />}
                </form>
            }

            footer={
                <div className='buttons-block buttons-block--from-right'>
                    <DefaultCloseSubWindowButton
                        text={submitText(vm.isEditMode)}
                        modifier={['fat', 'confirm-safe']}
                        onClick={() => vm.submitForm()}
                    />
                    <DefaultCloseSubWindowButton text={cancelText()} />
                </div>
            }
        />
    );
});
